import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { motion, useInView } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  TrendingUp, ArrowRight, Play, CheckCircle, BarChart3, Brain, Shield, 
  Lock, LineChart, Coins, Building2, ChevronRight, Zap, AlertTriangle,
  X, Users, Globe, Award, Sparkles, Bot, ArrowUpRight, Star, Rocket,
  Activity, Target, Cpu, Database, Clock, BookOpen, PieChart, Key,
  Server, Link2, FileText, Calculator, TrendingDown, CandlestickChart,
  Smartphone, Download, Fingerprint, Bell, Wifi
} from 'lucide-react';
import { LanguageSelector, LanguageSelectorFooter } from '@/components/LanguageSelector';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Animated Candlestick Chart Component
const AnimatedCandlestickChart = () => {
  const [candles, setCandles] = useState([]);
  
  useEffect(() => {
    // Generate initial candles
    const generateCandles = () => {
      const newCandles = [];
      let lastClose = 100;
      for (let i = 0; i < 24; i++) {
        const volatility = Math.random() * 8 + 2;
        const direction = Math.random() > 0.45 ? 1 : -1;
        const open = lastClose;
        const close = open + (direction * volatility * Math.random());
        const high = Math.max(open, close) + Math.random() * 3;
        const low = Math.min(open, close) - Math.random() * 3;
        newCandles.push({ open, close, high, low, bullish: close > open });
        lastClose = close;
      }
      return newCandles;
    };
    
    setCandles(generateCandles());
    
    // Animate candles periodically
    const interval = setInterval(() => {
      setCandles(prev => {
        const newCandles = [...prev.slice(1)];
        const lastCandle = prev[prev.length - 1];
        const volatility = Math.random() * 6 + 2;
        const direction = Math.random() > 0.45 ? 1 : -1;
        const open = lastCandle.close;
        const close = open + (direction * volatility * Math.random());
        const high = Math.max(open, close) + Math.random() * 2;
        const low = Math.min(open, close) - Math.random() * 2;
        newCandles.push({ open, close, high, low, bullish: close > open });
        return newCandles;
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  const maxPrice = Math.max(...candles.map(c => c.high), 120);
  const minPrice = Math.min(...candles.map(c => c.low), 80);
  const priceRange = maxPrice - minPrice;
  
  const scaleY = (price) => ((maxPrice - price) / priceRange) * 180;
  
  return (
    <div className="relative w-full h-48">
      {/* Grid lines */}
      <div className="absolute inset-0">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="absolute w-full border-t border-white/5" style={{ top: `${i * 25}%` }} />
        ))}
      </div>
      
      {/* Candles */}
      <svg className="w-full h-full" viewBox="0 0 480 180" preserveAspectRatio="none">
        {candles.map((candle, i) => {
          const x = i * 20 + 10;
          const wickTop = scaleY(candle.high);
          const wickBottom = scaleY(candle.low);
          const bodyTop = scaleY(Math.max(candle.open, candle.close));
          const bodyBottom = scaleY(Math.min(candle.open, candle.close));
          const bodyHeight = Math.max(bodyBottom - bodyTop, 2);
          
          return (
            <motion.g 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02, duration: 0.3 }}
            >
              {/* Wick */}
              <line
                x1={x}
                y1={wickTop}
                x2={x}
                y2={wickBottom}
                stroke={candle.bullish ? '#22c55e' : '#ef4444'}
                strokeWidth="1"
              />
              {/* Body */}
              <rect
                x={x - 6}
                y={bodyTop}
                width="12"
                height={bodyHeight}
                fill={candle.bullish ? '#22c55e' : '#ef4444'}
                rx="1"
                className="drop-shadow-lg"
              />
            </motion.g>
          );
        })}
        
        {/* Moving average line */}
        <motion.path
          d={`M ${candles.map((c, i) => `${i * 20 + 10},${scaleY((c.open + c.close) / 2)}`).join(' L ')}`}
          fill="none"
          stroke="url(#maGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
        />
        
        <defs>
          <linearGradient id="maGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

// Trading Dashboard Mockup Component
const TradingDashboardMockup = () => {
  const [activePrice, setActivePrice] = useState(94561.23);
  const [priceChange, setPriceChange] = useState(2.34);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePrice(prev => {
        const change = (Math.random() - 0.48) * 50;
        return prev + change;
      });
      setPriceChange(prev => {
        const change = (Math.random() - 0.5) * 0.1;
        return Math.max(-5, Math.min(5, prev + change));
      });
    }, 1500);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="relative">
      {/* Outer glow */}
      <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/30 via-cyan-500/20 to-purple-500/30 blur-3xl rounded-3xl opacity-50" />
      
      {/* Main Dashboard Card */}
      <motion.div 
        className="relative backdrop-blur-xl bg-slate-900/80 rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
        initial={{ rotateX: 10, rotateY: -10 }}
        animate={{ rotateX: 0, rotateY: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
      >
        {/* Top Bar */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
            </div>
            <span className="text-sm text-slate-400 font-mono">Trading Terminal</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              DEMO
            </span>
          </div>
        </div>
        
        {/* Chart Area */}
        <div className="p-6">
          {/* Ticker Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <span className="text-white font-bold text-lg">₿</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">BTC/USD</h3>
                <p className="text-sm text-slate-400">Bitcoin • Spot</p>
              </div>
            </div>
            <div className="text-right">
              <motion.p 
                key={activePrice}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-bold text-white font-mono"
              >
                ${activePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </motion.p>
              <p className={`text-sm font-medium ${priceChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}% (24h)
              </p>
            </div>
          </div>
          
          {/* Candlestick Chart */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
            <AnimatedCandlestickChart />
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: '24h High', value: '$95,420', color: 'text-emerald-400' },
              { label: '24h Low', value: '$92,150', color: 'text-red-400' },
              { label: 'Volume', value: '$48.2B', color: 'text-cyan-400' },
              { label: 'Open Interest', value: '$12.8B', color: 'text-purple-400' }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="bg-slate-800/30 rounded-lg p-3 text-center"
              >
                <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
                <p className={`font-mono font-semibold ${stat.color}`}>{stat.value}</p>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Order Panel Preview */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="py-3 rounded-xl bg-emerald-500 text-white font-semibold shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
            >
              <TrendingUp className="w-5 h-5" />
              Long
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="py-3 rounded-xl bg-red-500 text-white font-semibold shadow-lg shadow-red-500/25 flex items-center justify-center gap-2"
            >
              <TrendingDown className="w-5 h-5" />
              Short
            </motion.button>
          </div>
        </div>
      </motion.div>
      
      {/* Floating Mini Charts */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute -left-16 top-1/4 backdrop-blur-xl bg-slate-900/80 rounded-xl border border-white/10 p-3 shadow-xl"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center">
            <span className="text-blue-400 text-xs font-bold">E</span>
          </div>
          <span className="text-white text-sm font-medium">ETH</span>
        </div>
        <p className="text-emerald-400 text-xs font-mono">+1.87%</p>
        <div className="w-20 h-8 mt-1">
          <svg viewBox="0 0 80 32" className="w-full h-full">
            <path
              d="M0 28 L10 24 L20 26 L30 20 L40 22 L50 16 L60 18 L70 10 L80 14"
              fill="none"
              stroke="#22c55e"
              strokeWidth="2"
            />
          </svg>
        </div>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute -right-12 top-1/3 backdrop-blur-xl bg-slate-900/80 rounded-xl border border-white/10 p-3 shadow-xl"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center">
            <span className="text-purple-400 text-xs font-bold">S</span>
          </div>
          <span className="text-white text-sm font-medium">SOL</span>
        </div>
        <p className="text-emerald-400 text-xs font-mono">+4.21%</p>
        <div className="w-20 h-8 mt-1">
          <svg viewBox="0 0 80 32" className="w-full h-full">
            <path
              d="M0 30 L10 26 L20 28 L30 18 L40 20 L50 12 L60 14 L70 6 L80 8"
              fill="none"
              stroke="#22c55e"
              strokeWidth="2"
            />
          </svg>
        </div>
      </motion.div>
      
      {/* Floating Alert Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute -right-8 bottom-1/4 backdrop-blur-xl bg-emerald-500/20 rounded-lg border border-emerald-500/30 p-3 shadow-xl"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-emerald-400 text-xs font-semibold">Signal Detected</p>
            <p className="text-slate-400 text-xs">BTC Breakout +2.3%</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Video Demo Modal
const DemoVideoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-4xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute -top-12 right-0 text-white hover:bg-white/20"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </Button>
        <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <div className="p-5 border-b border-white/10">
            <h3 className="font-display text-xl font-bold text-white">Platform Overview</h3>
            <p className="text-sm text-slate-400">Learn how BitTrade HQ can support your trading research</p>
          </div>
          <div className="aspect-video bg-black">
            <iframe
              width="100%"
              height="100%"
              src="https://share.synthesia.io/embeds/videos/fdfd2832-f74b-415d-8604-b07dbe84e9ff"
              title="BitTrade HQ Platform Overview"
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Live ticker hook
const useLiveTickers = () => {
  const [tickers, setTickers] = useState([
    { pair: 'BTC/USD', price: 94561, change_24h: 2.34 },
    { pair: 'ETH/USD', price: 3283, change_24h: 1.87 },
    { pair: 'AAPL', price: 198.45, change_24h: 0.52 },
    { pair: 'MSFT', price: 412.30, change_24h: -0.31 },
    { pair: 'NVDA', price: 875.22, change_24h: 3.18 },
    { pair: 'SOL/USD', price: 187.45, change_24h: 4.21 },
    { pair: 'XRP/USD', price: 2.18, change_24h: 1.92 },
    { pair: 'TSLA', price: 248.50, change_24h: -0.85 }
  ]);

  useEffect(() => {
    const fetchTickers = async () => {
      try {
        const response = await axios.get(`${API}/market/tickers`);
        if (response.data?.length) setTickers(response.data);
      } catch (error) {
        console.log('Using default tickers');
      }
    };
    fetchTickers();
    const interval = setInterval(fetchTickers, 30000);
    return () => clearInterval(interval);
  }, []);

  return tickers;
};

// Animated Counter
const AnimatedCounter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, end]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Premium Navigation
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-slate-950/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight">
            <span className="text-white">BitTrade</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> HQ</span>
          </span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Features</a>
          <a href="#markets" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Markets</a>
          <Link to="/pricing" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Pricing</Link>
          <Link to="/broker-guide" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Connect Broker</Link>
        </div>
        
        <div className="flex items-center gap-3">
          <LanguageSelector variant="icon" />
          <Link to="/login">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10">
              Sign In
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all px-6">
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

// Live Market Ticker
const MarketTicker = ({ tickers }) => (
  <div className="bg-slate-900/80 backdrop-blur-xl border-y border-white/5">
    <Marquee gradient={false} speed={40} className="py-3">
      {[...tickers, ...tickers].map((t, i) => (
        <div key={i} className="flex items-center gap-6 mx-8">
          <span className="font-mono text-sm text-slate-300">{t.pair}</span>
          <span className="font-mono text-sm text-white font-semibold">
            ${typeof t.price === 'number' ? t.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : t.price}
          </span>
          <span className={`font-mono text-sm font-medium ${t.change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {t.change_24h >= 0 ? '+' : ''}{t.change_24h?.toFixed(2)}%
          </span>
        </div>
      ))}
    </Marquee>
  </div>
);

// Hero Section - Compliance Focused
const HeroSection = ({ onWatchDemo }) => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-slate-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(6,182,212,0.1),transparent_50%)]" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '64px 64px'
      }} />
      
      {/* Floating Orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-left"
          >
            <motion.p variants={fadeInUp} className="text-slate-400 text-sm font-medium mb-4">
              Used by traders and researchers worldwide
            </motion.p>
            
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400">
                Institutional-Style
              </span>
              <br />
              <span className="text-white">Trading Intelligence</span>
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-xl text-slate-400 leading-relaxed mb-4 max-w-xl">
              Professional tools and analytics for systematic trading research across crypto, stocks, ETFs, and futures.
            </motion.p>
            
            <motion.p variants={fadeInUp} className="text-lg text-slate-500 leading-relaxed mb-10 max-w-xl">
              Research, analyze, and manage rule-based trading strategies using institutional-style tools designed for disciplined decision-making.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="h-14 px-8 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-lg font-semibold shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all group"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={onWatchDemo}
                className="h-14 px-8 border-white/20 text-white hover:bg-white/10 text-lg font-semibold group"
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                Watch Demo
              </Button>
            </motion.div>
            
            {/* Trust Badges */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-slate-400">Non-Custodial</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-slate-400">Your Keys, Your Control</span>
              </div>
              <div className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-cyan-400" />
                <span className="text-sm text-slate-400">Multi-Exchange</span>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Right Content - Trading Dashboard Mockup */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            <TradingDashboardMockup />
          </motion.div>
        </div>
        
        {/* Risk Disclosure Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-12 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl"
        >
          <p className="text-amber-200/80 text-sm text-center">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>Risk Disclosure:</strong> Trading involves substantial risk and may result in loss of capital. This platform provides software tools and educational resources only. We do not provide investment advice.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Exchange Partners Section
const ExchangePartners = () => {
  const exchanges = ['Kraken', 'Alpaca', 'Coinbase', 'Binance', 'Bybit', 'Uphold'];

  return (
    <section className="py-12 bg-slate-900/50 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-center text-sm text-slate-500 mb-8">Connect Your Preferred Exchange</p>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
          {exchanges.map((exchange, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="text-2xl font-bold text-slate-600 hover:text-white transition-colors cursor-pointer"
            >
              {exchange}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Live Social Proof Stats Section
const SocialProofStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/public/stats`);
        setStats(res.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const statItems = stats ? [
    { value: stats.users.total, label: 'Active Traders', icon: Users, color: 'text-blue-400' },
    { value: stats.trades.total, label: 'Trades Executed', icon: Activity, color: 'text-emerald-400' },
    { value: stats.signals.total, label: 'AI Signals Generated', icon: Zap, color: 'text-purple-400' },
    { value: stats.bots.total, label: 'Active Bots', icon: Bot, color: 'text-cyan-400' }
  ] : [];

  return (
    <section className="py-16 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Live Platform Stats
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Trusted by Traders <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Worldwide</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {loading ? (
            // Loading skeleton
            [...Array(4)].map((_, i) => (
              <div key={i} className="bg-slate-800/50 rounded-2xl p-6 animate-pulse">
                <div className="w-12 h-12 bg-slate-700 rounded-xl mb-4" />
                <div className="h-8 bg-slate-700 rounded w-24 mb-2" />
                <div className="h-4 bg-slate-700 rounded w-32" />
              </div>
            ))
          ) : (
            statItems.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-800/30 backdrop-blur-xl rounded-2xl border border-white/5 p-6 text-center hover:border-white/10 transition-all"
              >
                <div className={`w-12 h-12 mx-auto rounded-xl bg-white/5 flex items-center justify-center mb-4 ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <motion.p 
                  className="text-3xl md:text-4xl font-bold text-white mb-1"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                >
                  {stat.value.toLocaleString()}+
                </motion.p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

// Testimonials Carousel Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/public/testimonials`);
        setTestimonials(res.data.testimonials);
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
        // Fallback testimonials
        setTestimonials([
          { id: 1, name: "Michael R.", role: "Day Trader", avatar: "MR", rating: 5, text: "The AI signals have completely transformed my trading approach.", highlight: "Win rate improved", verified: true },
          { id: 2, name: "Sarah K.", role: "Crypto Investor", avatar: "SK", rating: 5, text: "Finally a platform that covers crypto, stocks, AND forex.", highlight: "Multi-asset coverage", verified: true },
          { id: 3, name: "David L.", role: "Portfolio Manager", avatar: "DL", rating: 5, text: "Enterprise-grade risk management tools.", highlight: "Professional grade", verified: true }
        ]);
      }
    };
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  if (testimonials.length === 0) return null;

  return (
    <section className="py-20 bg-slate-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-4">
            <Star className="w-4 h-4 fill-amber-400" />
            Customer Reviews
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">Traders</span> Say
          </h2>
          <p className="text-slate-400">Join thousands of satisfied traders worldwide</p>
        </motion.div>

        {/* Testimonial Cards */}
        <div className="relative">
          <div className="flex gap-6 overflow-hidden">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ 
                  opacity: i === activeIndex ? 1 : 0.3,
                  scale: i === activeIndex ? 1 : 0.95,
                  x: 0
                }}
                transition={{ duration: 0.5 }}
                className={`flex-shrink-0 w-full md:w-1/2 lg:w-1/3 ${i === activeIndex ? 'z-10' : 'z-0'}`}
              >
                <div className={`bg-slate-900/50 backdrop-blur-xl rounded-2xl border ${i === activeIndex ? 'border-amber-500/30' : 'border-white/5'} p-6 h-full transition-all`}>
                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <p className="text-slate-300 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  
                  {/* Highlight Badge */}
                  {testimonial.highlight && (
                    <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-4">
                      {testimonial.highlight}
                    </span>
                  )}
                  
                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="text-white font-medium flex items-center gap-2">
                        {testimonial.name}
                        {testimonial.verified && (
                          <CheckCircle className="w-4 h-4 text-blue-400" />
                        )}
                      </p>
                      <p className="text-slate-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Navigation Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === activeIndex ? 'bg-amber-400 w-6' : 'bg-slate-600 hover:bg-slate-500'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section - Compliance Focused
const FeaturesSection = () => {
  const features = [
    {
      icon: Globe,
      title: 'Multi-Asset Market Coverage',
      description: 'Analyze crypto, stocks, ETFs, forex, and futures from a unified research dashboard.',
      gradient: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      icon: Brain,
      badge: 'AI',
      title: 'AI-Assisted Market Insights',
      description: 'Data-driven indicators and probabilistic insights designed to support research and strategy evaluation. Insights are informational only.',
      gradient: 'from-purple-500/20 to-pink-500/20'
    },
    {
      icon: BookOpen,
      title: 'Trading Journal & Analytics',
      description: 'Track activity, review performance patterns, and improve decision-making through structured analysis.',
      gradient: 'from-emerald-500/20 to-teal-500/20'
    },
    {
      icon: Target,
      title: 'Strategy Frameworks',
      description: 'Build, test, and evaluate rule-based trading methodologies using configurable logic and historical market data.',
      gradient: 'from-orange-500/20 to-amber-500/20'
    },
    {
      icon: Calculator,
      title: 'Risk Management Tools',
      description: 'Position sizing models, drawdown analysis, volatility metrics, and risk exposure insights. Risk tools do not eliminate risk.',
      gradient: 'from-red-500/20 to-rose-500/20'
    },
    {
      icon: Key,
      title: 'Non-Custodial by Design',
      description: 'We never hold customer funds or assets. Your accounts and capital remain entirely under your control.',
      gradient: 'from-cyan-500/20 to-blue-500/20'
    }
  ];

  return (
    <section id="features" className="relative py-24 bg-slate-950">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(6,182,212,0.1),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-4">
            Platform Features
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Professional Trading <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Tools</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Software tools for traders who want structure, discipline, and data-driven insights.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="group relative backdrop-blur-xl bg-slate-900/50 rounded-2xl border border-white/10 p-8 hover:border-white/20 transition-all hover:shadow-xl hover:shadow-blue-500/5"
            >
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity`} />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <feature.icon className="w-7 h-7 text-blue-400" />
                  </div>
                  {feature.badge && (
                    <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 text-xs font-bold">
                      {feature.badge}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Important Disclaimer Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 p-6 bg-slate-900/80 border border-amber-500/20 rounded-2xl"
        >
          <h4 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Important: What BitTrade HQ Is NOT
          </h4>
          <p className="text-slate-400 leading-relaxed">
            We do not manage funds, execute trades on behalf of users, or provide investment advice. All automation tools operate under user configuration and control. BitTrade HQ does not place or manage trades. <strong className="text-white">If you're looking for guaranteed profits or hands-off trading, this platform is not for you.</strong>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Why BitTradeHQ Comparison Section
const ComparisonSection = () => {
  const competitors = ['3Commas', 'Cryptohopper', 'Basic Bots'];
  
  const features = [
    { name: 'Custody of Funds', bittrade: { value: 'Never', icon: 'shield' }, competitors: ['No', 'No', 'No'] },
    { name: 'Multi-Exchange Support', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['Yes', 'Yes', 'Limited'] },
    { name: 'Grid Bots', bittrade: { value: 'Unlimited*', icon: 'check' }, competitors: ['Limited', 'Limited', 'Basic'] },
    { name: 'DCA Bots', bittrade: { value: 'Advanced', icon: 'check' }, competitors: ['Limited', 'Limited', 'No'] },
    { name: 'Smart Manual Trades', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['Yes', 'No', 'No'] },
    { name: 'Futures Trading', bittrade: { value: 'Included', icon: 'check' }, competitors: ['Add-on', 'No', 'No'] },
    { name: 'AI Strategy Builder', bittrade: { value: 'Included', icon: 'ai' }, competitors: ['No', 'No', 'No'] },
    { name: 'AI Trading Assistant', bittrade: { value: 'Included', icon: 'ai' }, competitors: ['No', 'No', 'No'] },
    { name: 'Portfolio Risk Management', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['No', 'Partial', 'No'] },
    { name: 'Multi-Account Management', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['Paid Add-on', 'Paid Add-on', 'No'] },
    { name: 'Developer API (Full)', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['Restricted', 'Limited', 'No'] },
    { name: 'White-Label Capability', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['No', 'No', 'No'] },
    { name: 'Enterprise / Fund Ready', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['Partial', 'No', 'No'] },
    { name: 'Built for Institutions', bittrade: { value: 'Yes', icon: 'check' }, competitors: ['No', 'No', 'No'] },
  ];

  const getStatusIcon = (value) => {
    if (value === 'Yes' || value === 'Included' || value === 'Advanced' || value === 'Unlimited*' || value === 'Never') {
      return <CheckCircle className="w-5 h-5 text-emerald-400" />;
    }
    if (value === 'No') {
      return <X className="w-4 h-4 text-red-400" />;
    }
    if (value === 'Limited' || value === 'Partial' || value === 'Basic' || value === 'Restricted') {
      return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    }
    if (value === 'Paid Add-on' || value === 'Add-on') {
      return <span className="text-amber-400 text-xs">$</span>;
    }
    return null;
  };

  const differentiators = [
    {
      icon: Database,
      title: 'Infrastructure, Not Just Tools',
      description: 'While other platforms focus on single strategies and feature limits, BitTradeHQ was built to support multi-strategy portfolios, risk-first automation, and institutional deployment.'
    },
    {
      icon: Shield,
      title: 'Non-Custodial by Design',
      description: 'We never hold your money. You connect directly to your exchange via API. Trades execute under your control—the same model used by professional trading desks and quant funds.'
    },
    {
      icon: Rocket,
      title: 'Built to Scale With You',
      description: 'Start retail, scale to professional, upgrade to enterprise, launch white-label products. Most platforms cap your growth. BitTradeHQ grows with you.'
    },
    {
      icon: Zap,
      title: 'No Hidden Upsells',
      description: 'At comparable pricing, BitTradeHQ includes AI strategy creation, futures automation, full API access, and multi-account support—features competitors lock behind add-ons.'
    }
  ];

  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08),transparent_60%)]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-4">
            Why BitTradeHQ
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            BitTradeHQ vs <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">The Competition</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            See why serious traders choose BitTradeHQ over other trading platforms
          </p>
        </motion.div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="backdrop-blur-xl bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden mb-16"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-slate-400 font-medium">Feature</th>
                  <th className="py-4 px-4 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mb-2">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-cyan-400 font-bold">BitTradeHQ</span>
                    </div>
                  </th>
                  {competitors.map((comp, i) => (
                    <th key={i} className="py-4 px-4 text-center text-slate-500 font-medium">{comp}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 px-6 text-white text-sm">{feature.name}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {feature.bittrade.icon === 'ai' ? (
                          <Brain className="w-5 h-5 text-purple-400" />
                        ) : feature.bittrade.icon === 'shield' ? (
                          <Shield className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        )}
                        <span className="text-emerald-400 text-sm font-medium">{feature.bittrade.value}</span>
                      </div>
                    </td>
                    {feature.competitors.map((val, j) => (
                      <td key={j} className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {getStatusIcon(val)}
                          <span className={`text-xs ${
                            val === 'No' ? 'text-red-400' :
                            val === 'Yes' ? 'text-slate-400' :
                            'text-amber-400'
                          }`}>{val}</span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 bg-slate-800/30 border-t border-white/5">
            <p className="text-xs text-slate-500">* Unlimited on higher tiers. See pricing for details.</p>
          </div>
        </motion.div>

        {/* Key Differentiators */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Most platforms sell <span className="text-slate-500">bots</span>. BitTradeHQ delivers <span className="text-cyan-400">infrastructure</span>.
            </h3>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group p-6 rounded-xl bg-slate-900/50 border border-white/10 hover:border-cyan-500/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                    <item.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Value Proposition Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20"
          >
            <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <h4 className="text-lg font-bold text-white mb-3">Included, Not Extra</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> AI-powered strategy creation</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Futures trading automation</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Advanced DCA & grid logic</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Full developer API access</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Multi-account support</li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20"
          >
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-purple-400" />
            </div>
            <h4 className="text-lg font-bold text-white mb-3">Institutional Architecture</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Professional trading desk model</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Quant fund standards</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Asset manager workflows</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Enterprise-ready APIs</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> White-label deployment</li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="p-6 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20"
          >
            <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <h4 className="text-lg font-bold text-white mb-3">Full Control</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> Non-custodial always</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> Direct exchange connection</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> API-only execution</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> Your keys, your funds</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> Transparent pricing</li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Line CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center p-8 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 border border-white/10"
        >
          <h3 className="text-2xl font-bold text-white mb-4">The Bottom Line</h3>
          <p className="text-slate-400 max-w-2xl mx-auto mb-6">
            If you only want a basic bot, a $20 platform may work. If you want <span className="text-white">serious automation</span>, 
            <span className="text-white"> risk control</span>, and <span className="text-white">scale</span>—BitTradeHQ delivers more power, 
            more flexibility, and more long-term value at a price that still undercuts most competitors.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8">
              <Rocket className="w-5 h-5 mr-2" />
              Start Your Free Trial
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Platform Preview Section with Trading Illustrations
const PlatformPreviewSection = () => {
  // Generate animated area chart data
  const generateChartPath = (points, height, variance) => {
    const data = [];
    let lastY = height / 2;
    for (let i = 0; i < points; i++) {
      const change = (Math.random() - 0.45) * variance;
      lastY = Math.max(10, Math.min(height - 10, lastY + change));
      data.push({ x: (i / (points - 1)) * 400, y: lastY });
    }
    return data;
  };

  const [chartData] = useState(() => generateChartPath(50, 120, 15));
  
  const pathD = `M ${chartData.map(p => `${p.x},${p.y}`).join(' L ')}`;
  const areaD = `${pathD} L 400,120 L 0,120 Z`;

  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(59,130,246,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(168,85,247,0.08),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-4">
            Platform Preview
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Advanced Trading <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Analytics</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Powerful visualization tools for market analysis and strategy development
          </p>
        </motion.div>

        {/* Trading Dashboard Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          {/* Main Chart Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-6 overflow-hidden"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <LineChart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Portfolio Performance</h3>
                  <p className="text-sm text-slate-400">Real-time tracking</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {['1D', '1W', '1M', '1Y'].map((period, i) => (
                  <button
                    key={period}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      i === 2 ? 'bg-blue-500 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Chart Area */}
            <div className="relative h-48">
              <svg className="w-full h-full" viewBox="0 0 400 120" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {/* Grid */}
                {[0, 1, 2, 3, 4].map(i => (
                  <line key={i} x1="0" y1={i * 30} x2="400" y2={i * 30} stroke="rgba(255,255,255,0.05)" />
                ))}
                {/* Area */}
                <motion.path
                  d={areaD}
                  fill="url(#chartGradient)"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1 }}
                />
                {/* Line */}
                <motion.path
                  d={pathD}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 2, ease: "easeOut" }}
                />
              </svg>
              
              {/* Overlay Stats */}
              <div className="absolute top-4 left-4 backdrop-blur bg-slate-900/80 rounded-lg p-3 border border-white/10">
                <p className="text-xs text-slate-400">Total Return</p>
                <p className="text-2xl font-bold text-emerald-400">+127.5%</p>
              </div>
            </div>
          </motion.div>

          {/* Side Stats Cards */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-slate-400 text-sm">Win Rate</span>
              </div>
              <p className="text-4xl font-bold text-white mb-2">68.4%</p>
              <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: '68.4%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.3 }}
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <PieChart className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-slate-400 text-sm">Sharpe Ratio</span>
              </div>
              <p className="text-4xl font-bold text-white mb-2">2.34</p>
              <p className="text-sm text-emerald-400">Above benchmark</p>
            </motion.div>
          </div>
        </div>

        {/* Bottom Cards Row */}
        <div className="grid md:grid-cols-4 gap-6">
          {/* Mini Candlestick Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-semibold">BTC/USD</span>
              <span className="text-emerald-400 text-sm font-mono">+2.34%</span>
            </div>
            <div className="h-20">
              <svg viewBox="0 0 120 60" className="w-full h-full">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
                  const x = i * 15 + 7;
                  const bullish = Math.random() > 0.4;
                  const bodyTop = 15 + Math.random() * 15;
                  const bodyHeight = 10 + Math.random() * 10;
                  return (
                    <g key={i}>
                      <line x1={x} y1={bodyTop - 5} x2={x} y2={bodyTop + bodyHeight + 5} stroke={bullish ? '#22c55e' : '#ef4444'} strokeWidth="1" />
                      <rect x={x - 4} y={bodyTop} width="8" height={bodyHeight} fill={bullish ? '#22c55e' : '#ef4444'} rx="1" />
                    </g>
                  );
                })}
              </svg>
            </div>
          </motion.div>

          {/* Volume Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-semibold">Volume</span>
              <span className="text-cyan-400 text-sm font-mono">$48.2B</span>
            </div>
            <div className="flex items-end gap-1 h-20">
              {[40, 60, 35, 80, 55, 70, 45, 90, 65, 75].map((h, i) => (
                <motion.div
                  key={i}
                  className="flex-1 bg-gradient-to-t from-cyan-500/50 to-cyan-500 rounded-t"
                  initial={{ height: 0 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                />
              ))}
            </div>
          </motion.div>

          {/* RSI Indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-semibold">RSI (14)</span>
              <span className="text-amber-400 text-sm font-mono">58.4</span>
            </div>
            <div className="relative h-20">
              <div className="absolute inset-x-0 top-1/4 h-px bg-red-500/30" />
              <div className="absolute inset-x-0 bottom-1/4 h-px bg-emerald-500/30" />
              <svg viewBox="0 0 120 60" className="w-full h-full">
                <path
                  d="M0 40 Q20 35, 40 42 T80 38 T120 30"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                />
              </svg>
              <div className="absolute top-0 right-0 text-[10px] text-red-400">Overbought</div>
              <div className="absolute bottom-0 right-0 text-[10px] text-emerald-400">Oversold</div>
            </div>
          </motion.div>

          {/* MACD */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="backdrop-blur-xl bg-slate-900/60 rounded-2xl border border-white/10 p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-semibold">MACD</span>
              <span className="text-emerald-400 text-sm font-mono">Bullish</span>
            </div>
            <div className="relative h-20">
              <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
              <div className="flex items-center justify-around h-full">
                {[-8, -4, 2, 6, 10, 8, 12, 15, 10, 6].map((v, i) => (
                  <div
                    key={i}
                    className={`w-2 rounded ${v > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                    style={{
                      height: `${Math.abs(v) * 3}px`,
                      marginTop: v > 0 ? 'auto' : 0,
                      marginBottom: v > 0 ? 0 : 'auto',
                      position: 'relative',
                      top: v > 0 ? '-50%' : '50%'
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: 2.4, suffix: 'B+', prefix: '$', label: 'Market Data Reviewed', sublabel: 'Strategy analysis & historical data' },
    { label: 'Global', sublabel: 'User Base', description: 'Traders & researchers worldwide' },
    { label: 'Enterprise', sublabel: 'Grade Reliability', description: 'High-availability infrastructure' },
    { value: 10, suffix: '+', label: 'Integrations', sublabel: 'Third-party connections' }
  ];

  return (
    <section className="relative py-20 bg-slate-900/50">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.05),transparent_70%)]" />
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Non-Custodial Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <Key className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-medium">Non-Custodial—We never hold your funds. Your keys, your assets — always.</span>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {stat.value ? (
                  <AnimatedCounter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                ) : (
                  stat.label
                )}
              </div>
              <p className="text-slate-400 font-medium">{stat.value ? stat.label : stat.sublabel}</p>
              <p className="text-slate-500 text-sm mt-1">{stat.sublabel || stat.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Multi-Asset Coverage Section
const MarketsSection = () => {
  const markets = [
    { icon: Coins, title: 'Cryptocurrencies', description: 'BTC, ETH, SOL, and major tokens' },
    { icon: LineChart, title: 'Stocks & ETFs', description: 'NYSE, NASDAQ market data' },
    { icon: TrendingUp, title: 'Forex', description: 'EUR/USD, GBP/USD and major pairs' },
    { icon: BarChart3, title: 'Futures', description: 'Futures market analysis tools' }
  ];

  return (
    <section id="markets" className="relative py-24 bg-slate-950">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(59,130,246,0.1),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-4">
              Multi-Asset Coverage
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Research Any Market,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">From One Platform</span>
            </h2>
            <p className="text-xl text-slate-400 mb-8">
              Whether you're researching Bitcoin, analyzing Apple stock, or studying S&P 500 futures, our platform provides the analytical tools you need.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {markets.map((market, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                >
                  <market.icon className="w-8 h-8 text-cyan-400 mb-3" />
                  <h3 className="font-semibold text-white mb-1">{market.title}</h3>
                  <p className="text-sm text-slate-400">{market.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-3xl rounded-full" />
            <img 
              src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80" 
              alt="Professional trading research setup"
              className="relative rounded-2xl border border-white/10 shadow-2xl"
            />
            <div className="absolute -bottom-4 -right-4 px-4 py-2 rounded-lg bg-slate-900/90 border border-white/10 backdrop-blur-xl">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-white font-medium">Non-Custodial</span>
              </div>
              <p className="text-xs text-slate-400">Your Keys, Your Control</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Target Audience Section
const AudienceSection = () => {
  const audiences = [
    'Independent Traders',
    'Quant-Curious Professionals',
    'Systematic Strategy Researchers',
    'Data-Driven Market Participants'
  ];

  return (
    <section className="relative py-24 bg-slate-900">
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12">
            Who Is BitTrade HQ <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">For?</span>
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {audiences.map((audience, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="p-4 rounded-xl bg-white/5 border border-white/10"
              >
                <p className="text-white font-medium">{audience}</p>
              </motion.div>
            ))}
          </div>
          
          <p className="text-lg text-amber-400 font-medium">
            <strong>Important:</strong> If you're looking for guaranteed profits or hands-off trading, this platform is not for you.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10" />
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Ready to Elevate Your<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Trading Research?</span>
          </h2>
          <p className="text-xl text-slate-400 mb-4 max-w-2xl mx-auto">
            BitTrade HQ is designed for independent traders, quant-curious professionals, and systematic strategy researchers.
          </p>
          <p className="text-lg text-amber-400 mb-10">
            If you're looking for guaranteed profits or hands-off trading, this platform is not for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg"
              onClick={() => navigate('/register')}
              className="h-14 px-10 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-lg font-semibold shadow-xl shadow-blue-500/25"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/pricing')}
              className="h-14 px-10 border-white/20 text-white hover:bg-white/10 text-lg font-semibold"
            >
              View Pricing
            </Button>
          </div>
          <p className="text-slate-500">
            7-day free trial • No credit card required • Cancel anytime
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Download App Section
const DownloadAppSection = () => (
  <section className="relative py-24 overflow-hidden" data-testid="download-app-section">
    {/* Background */}
    <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px]" />
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px]" />
    
    <div className="relative max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        {/* Left: Content */}
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Smartphone className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-400 font-medium">Available on All Devices</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Trade Anywhere.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Institutional Power in Your Pocket.
            </span>
          </h2>
          
          <p className="text-lg text-slate-400 mb-8 max-w-lg">
            Access your AI trading terminal from any device. Real-time signals, portfolio management, and autonomous bots — all at your fingertips.
          </p>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 mb-10">
            {[
              { icon: Bell, label: 'Push Notifications' },
              { icon: Wifi, label: 'Offline Access' },
              { icon: Fingerprint, label: 'Biometric Login' },
              { icon: Zap, label: 'Instant Loading' }
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <Icon className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-xs text-slate-300 font-medium">{label}</span>
              </div>
            ))}
          </div>

          {/* Store Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* App Store */}
            <a 
              href="#" 
              className="group flex items-center gap-4 px-6 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl transition-all"
              data-testid="app-store-btn"
            >
              <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Download on the</div>
                <div className="text-lg font-semibold text-white -mt-0.5">App Store</div>
              </div>
            </a>

            {/* Google Play */}
            <a 
              href="#" 
              className="group flex items-center gap-4 px-6 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl transition-all"
              data-testid="google-play-btn"
            >
              <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Get it on</div>
                <div className="text-lg font-semibold text-white -mt-0.5">Google Play</div>
              </div>
            </a>
          </div>

          <p className="text-xs text-slate-500 mt-4">
            Also available as a web app — install directly from your browser. No app store required.
          </p>
        </div>

        {/* Right: Phone Mockup */}
        <div className="relative flex justify-center lg:justify-end">
          <div className="relative">
            {/* Phone frame */}
            <div className="w-[280px] h-[560px] rounded-[3rem] border-[8px] border-slate-700 bg-slate-900 shadow-2xl shadow-cyan-500/10 overflow-hidden relative">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-700 rounded-b-2xl z-10" />
              
              {/* Screen content mockup */}
              <div className="h-full bg-gradient-to-b from-slate-950 to-slate-900 p-4 pt-10">
                {/* Status bar */}
                <div className="flex justify-between text-[10px] text-slate-400 mb-4 px-2">
                  <span>9:41</span>
                  <div className="flex gap-1 items-center">
                    <div className="w-4 h-2 border border-slate-400 rounded-sm"><div className="w-3 h-1 bg-emerald-400 rounded-sm m-px" /></div>
                  </div>
                </div>

                {/* App header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-bold text-white">BitTrade HQ</span>
                </div>

                {/* Portfolio card */}
                <div className="bg-white/5 rounded-xl p-3 mb-3 border border-white/10">
                  <div className="text-[10px] text-slate-400 mb-1">Portfolio Value</div>
                  <div className="text-xl font-bold text-white">$124,582.40</div>
                  <div className="text-xs text-emerald-400 font-mono">+12.4% (24h)</div>
                </div>

                {/* Signal card */}
                <div className="bg-emerald-500/10 rounded-xl p-3 mb-3 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                      <TrendingUp className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className="text-xs font-bold text-emerald-400">STRONG BUY</span>
                    <span className="text-[10px] text-slate-400 ml-auto">95% conf.</span>
                  </div>
                  <div className="text-sm font-semibold text-white">BTC/USD @ $68,850</div>
                </div>

                {/* Active bots */}
                <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                  <div className="text-[10px] text-slate-400 mb-2">Active Bots</div>
                  {[
                    { name: 'Grid BTC', profit: '+8.2%', color: 'emerald' },
                    { name: 'DCA ETH', profit: '+5.1%', color: 'cyan' }
                  ].map(bot => (
                    <div key={bot.name} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full bg-${bot.color}-400`} />
                        <span className="text-xs text-white">{bot.name}</span>
                      </div>
                      <span className="text-xs font-mono text-emerald-400">{bot.profit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating badges around phone */}
            <div className="absolute -top-4 -left-8 px-3 py-1.5 bg-slate-800 border border-white/10 rounded-full shadow-lg animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
              <span className="text-xs text-emerald-400 font-medium">Live Signals</span>
            </div>
            <div className="absolute top-1/3 -right-12 px-3 py-1.5 bg-slate-800 border border-white/10 rounded-full shadow-lg animate-bounce" style={{ animationDelay: '1s', animationDuration: '3s' }}>
              <span className="text-xs text-cyan-400 font-medium">AI Powered</span>
            </div>
            <div className="absolute bottom-1/4 -left-12 px-3 py-1.5 bg-slate-800 border border-white/10 rounded-full shadow-lg animate-bounce" style={{ animationDelay: '2s', animationDuration: '3s' }}>
              <span className="text-xs text-amber-400 font-medium">24/7 Bots</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-slate-950 border-t border-white/5 py-16">
    <div className="max-w-7xl mx-auto px-6">
      {/* Agent/Affiliate CTA Banner */}
      <div className="mb-12 p-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Become an Affiliate</h3>
              <p className="text-sm text-slate-400">Earn 20-30% recurring commissions promoting BitTrade HQ</p>
            </div>
          </div>
          <Link 
            to="/affiliate" 
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            Apply Now
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-12 mb-12">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-white">BitTrade HQ</span>
          </Link>
          <p className="text-slate-400 text-sm leading-relaxed">
            Professional tools and analytics for systematic trading research across crypto, stocks, ETFs, and futures.
          </p>
        </div>
        
        <div>
          <h4 className="font-semibold text-white mb-4">Product</h4>
          <ul className="space-y-3">
            <li><Link to="/pricing" className="text-slate-400 hover:text-white transition-colors text-sm">Pricing</Link></li>
            <li><Link to="/broker-guide" className="text-slate-400 hover:text-white transition-colors text-sm">Connect Broker</Link></li>
            <li><Link to="/education" className="text-slate-400 hover:text-white transition-colors text-sm">Education</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
            Affiliates
          </h4>
          <ul className="space-y-3">
            <li><Link to="/affiliate" className="text-amber-400 hover:text-amber-300 transition-colors text-sm font-medium">Affiliate Program</Link></li>
            <li><Link to="/affiliate#commissions" className="text-slate-400 hover:text-white transition-colors text-sm">Commission Rates</Link></li>
            <li><Link to="/affiliate#apply" className="text-slate-400 hover:text-white transition-colors text-sm">Apply to Join</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            Enterprise
          </h4>
          <ul className="space-y-3">
            <li><Link to="/white-label" className="text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium">White-Label Platform</Link></li>
            <li><Link to="/white-label/demo" className="text-slate-400 hover:text-white transition-colors text-sm">View Demo</Link></li>
            <li><Link to="/platform" className="text-slate-400 hover:text-white transition-colors text-sm">Platform Info</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-semibold text-white mb-4">Legal & Support</h4>
          <ul className="space-y-3">
            <li><Link to="/legal-documents" className="text-slate-400 hover:text-white transition-colors text-sm">Legal Documents</Link></li>
            <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors text-sm">Terms of Service</Link></li>
            <li><Link to="/privacy" className="text-slate-400 hover:text-white transition-colors text-sm">Privacy Policy</Link></li>
            <li><a href="mailto:support@bittradehq.com" className="text-slate-400 hover:text-white transition-colors text-sm">Contact Support</a></li>
          </ul>
        </div>
      </div>
      
      <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-slate-500 text-sm">© 2026 Nova Media Ventures LLC. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <LanguageSelectorFooter />
          <span className="text-slate-700">|</span>
          <Link to="/affiliate" className="text-amber-400 hover:text-amber-300 text-sm font-medium flex items-center gap-1">
            <Users className="w-3 h-3" />
            Become an Affiliate
          </Link>
          <span className="text-slate-700">|</span>
          <Link to="/system-status" className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-emerald-400 text-sm font-medium">System Status</span>
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

// Main Landing Component
export default function Landing() {
  const [showDemo, setShowDemo] = useState(false);
  const tickers = useLiveTickers();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      <MarketTicker tickers={tickers} />
      <HeroSection onWatchDemo={() => setShowDemo(true)} />
      <ExchangePartners />
      <SocialProofStats />
      <FeaturesSection />
      <TestimonialsSection />
      <ComparisonSection />
      <PlatformPreviewSection />
      <StatsSection />
      <MarketsSection />
      <AudienceSection />
      <DownloadAppSection />
      <CTASection />
      <Footer />
      <DemoVideoModal isOpen={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
